import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import instance from '../../../instance';
import {ICreateDepartmentsRequest, IDeleteDepartmentsRequest, IGetDepartmentsRequest} from '../../../interfaces/fetch/department';
import {IDepartmenstData} from './type/IDepartmenstData';
import CallNotification from '../../../context/NotificationContext';

interface ITradePlacesPopupProps {
    open: boolean;
    onClose: () => void;
    userId: number | null;
}

const TradePlacesPopup = ({open, onClose, userId}: ITradePlacesPopupProps) => {
    const {handleErrorNotification} = CallNotification();
    const [departments, setDepartments] = useState<IDepartmenstData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState<{name: string; address: string}>({name: '', address: ''});
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValues, setEditValues] = useState<{name: string; address: string}>({name: '', address: ''});

    const nameRef = useRef<HTMLInputElement | null>(null);
    const addressRef = useRef<HTMLInputElement | null>(null);

    const fetchDepartments = async () => {
        if (userId == null) {
            return;
        }

        setLoading(true);
        try {
            const request: IGetDepartmentsRequest = {
                method: 'GetDepartment',
                params: {user_id: userId},
            };
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);
            setDepartments(response.data?.department || []);
        } catch (e) {
            console.error('Failed to fetch departments:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (userId == null) {
            return;
        }

        const name = nameRef.current?.value.trim() || '';
        const address = addressRef.current?.value.trim() || '';
        if (!name || !address) {
            setError({
                name: !name ? 'Введите название' : '',
                address: !address ? 'Введите адрес' : '',
            });
            return;
        }

        setAdding(true);

        try {
            const request: ICreateDepartmentsRequest = {
                method: 'CreateDepartment',
                params: {
                    user_id: userId,
                    name,
                    address,
                },
            };
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);
            await fetchDepartments();
            if (nameRef.current) nameRef.current.value = '';
            if (addressRef.current) addressRef.current.value = '';
        } catch (e) {
            console.error('Failed to create department:', e);
        } finally {
            setAdding(false);
        }
    };

    const handleSave = async (id: number) => {
        if (!editValues.name || !editValues.address) {
            setError({
                name: !editValues.name ? 'Введите название' : '',
                address: !editValues.address ? 'Введите адрес' : '',
            });
            return;
        }

        try {
            const response = await instance.post('/LK', {
                method: 'EditDepartment',
                params: {
                    user_id: userId,
                    department_id: id,
                    name: editValues.name,
                    address: editValues.address,
                },
            });
            handleErrorNotification(response);
            await fetchDepartments();
            setEditingId(null);
            setEditValues({name: '', address: ''});
        } catch (e) {
            console.error('Failed to edit department:', e);
        }
    };

    const handleDelete = async (id: number) => {
        if (userId == null) {
            return;
        }

        if (!window.confirm('Удалить место торговли?')) return;

        try {
            const request: IDeleteDepartmentsRequest = {
                method: 'DeleteDepartment',
                params: {user_id: userId, department_id: id},
            };
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);
            await fetchDepartments();
        } catch (e) {
            console.error('Failed to delete department:', e);
        }
    };

    useEffect(() => {
        if (open) void fetchDepartments();
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <div style={{padding: '20px 24px 10px 3px', position: 'relative', backgroundColor: '#f5f7ff'}}>
                <DialogTitle sx={{pr: 5}}>Места торговли мерчанта</DialogTitle>
                <IconButton onClick={onClose} style={{position: 'absolute', right: 16, top: 16}}>
                    <CloseIcon />
                </IconButton>
            </div>
            <DialogContent>
                <Box display="flex" gap={2} mb={2}>
                    <TextField label="Название" inputRef={nameRef} error={!!error.name} helperText={error.name} fullWidth />
                    <TextField label="Адрес" inputRef={addressRef} error={!!error.address} helperText={error.address} fullWidth />
                    <Button variant="contained" onClick={() => void handleAdd()} sx={{minWidth: '120px'}} disabled={adding}>
                        Добавить
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : departments.length === 0 ? (
                    <Box py={4} textAlign="center">
                        <Typography variant="body2" color="textSecondary">
                            Нет ни одного места торговли
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer
                        sx={{
                            maxHeight: 500,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Название</TableCell>
                                    <TableCell>Адрес</TableCell>
                                    <TableCell align="right">&nbsp;</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {departments.map(dep => {
                                    const isEditing = editingId === dep.id;
                                    return (
                                        <TableRow key={dep.id} sx={{'&:last-child td': {borderBottom: 0}, '&:hover .edit-icon': {visibility: 'visible'}}}>
                                            <TableCell>
                                                {isEditing ? (
                                                    <TextField
                                                        value={editValues.name}
                                                        onChange={e => setEditValues(prev => ({...prev, name: e.target.value}))}
                                                        error={!!error.name}
                                                        helperText={error.name}
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Box sx={{height: 40, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                        {dep.name}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {isEditing ? (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <TextField
                                                            value={editValues.address}
                                                            onChange={e => setEditValues(prev => ({...prev, address: e.target.value}))}
                                                            error={!!error.address}
                                                            helperText={error.address}
                                                            size="small"
                                                            fullWidth
                                                        />
                                                        <Tooltip title="Сохранить">
                                                            <IconButton
                                                                onClick={() => dep.id !== undefined && void handleSave(dep.id)}
                                                                color="primary"
                                                            >
                                                                <SaveOutlinedIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Отмена">
                                                            <IconButton
                                                                onClick={() => {
                                                                    setEditingId(null);
                                                                    setEditValues({name: '', address: ''});
                                                                    setError({name: '', address: ''});
                                                                }}
                                                            >
                                                                <CloseIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{height: 40, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                        {dep.address}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box className="edit-icon" sx={{visibility: 'hidden', display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                                                    <Tooltip title="Редактировать">
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingId(dep.id ?? null);
                                                                setEditValues({name: dep.name || '', address: dep.address || ''});
                                                                setError({name: '', address: ''});
                                                            }}
                                                        >
                                                            <EditOutlinedIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Удалить">
                                                        <IconButton onClick={() => dep.id !== undefined && void handleDelete(dep.id)}>
                                                            <DeleteOutlinedIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TradePlacesPopup;
